import {Space, message, Button, Modal} from "antd";
import React, {forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState} from "react";
import styles from "../reservation.module.css"
import request from "../../../libs/request";
import _, {get} from "lodash-es";
import dayjs from 'dayjs';
import intl from "react-intl-universal";
import Evisit from "../../../libs/Evisit";

const ReservationContext = React.createContext();

const useFetchData = (url) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const response = await request.get(url);
                if (response) {
                    setData(response);
                } else {
                    throw new Error('Error fetching data');
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [url, reload]);

    const refetchData = () => {
        setReload(dayjs().unix());
    };

    return {data, loading, error, refetchData};
};

const useDeleteData = (initUrl = '') => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const deleteData = async (url) => {
        setIsLoading(true);
        setError(null);

        try {
            const resp = await request.delete(url ? url : initUrl);
            setResult(resp)
        } catch (error) {
            setError(error);
        }

        setIsLoading(false);
    };

    return { state : { isLoading, error }, deleteData, result};
}

export const useSubmitData = (url = '') => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [result, setResult] = useState(null);

    const postData = async (submitData) => {
        setIsLoading(true);
        setError(null);

        try {

            const resp = await request.post(url, submitData ? submitData : data);
            setResult(resp);
        } catch (error) {
            setError(error);
        }

        setIsLoading(false);
    };

    const putData = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const resp = await request.put(url, data);
            setResult(resp);
        } catch (error) {
            setError(error);
        }

        setIsLoading(false);
    }

    return {state: { isLoading, error }, setData, postData, putData, result};
}

const displayError = (error) => {
    if (error) {
        message.error(
            get(error, 'response.data.message') || get(error, 'message') || 'An error occurred'
        );
    }

}

const getWaitingTime = (expiryTime) => {
    const currentTime = dayjs().unix();
    const diffInSecond = expiryTime - currentTime;
    const diffInMinutes = diffInSecond > 0 ? Math.floor(diffInSecond / 60).toString().padStart(2, '0') : '00';
    const remainingSeconds = diffInSecond > 0 ? (diffInSecond % 60).toString().padStart(2, '0') : '00';
    return `${diffInMinutes}:${remainingSeconds}`;
}

const getLocalSelectedSlot = (key) => {
    const localSelectedSlot = localStorage.getItem(key);

    try {
        return JSON.parse(localSelectedSlot);
    }
    catch (e) {
        return null;
    }
}

const Reservation = forwardRef((props, ref) => {
    const [messageApi, contextHolder] = message.useMessage();
    const key = useId();
    const confirmReservationRef = useRef(null);
    const {
        data: reservations,
        error,
        refetchData
    } = useFetchData(`${window.base_url}/api/reservations`);
    const { deleteData } = useDeleteData();

    const {
        postData: postCreateReservation,
        result: postResult
    } = useSubmitData(`${window.base_url}/api/reservations`)

    const handleReserveSlotExpired = (data, expired) => {
        if (confirmReservationRef.current) {
            confirmReservationRef.current.showModal(data, expired);
        }
    }

    const displayReservation = () => {
        let data = reservations.data;
        const localSelectedSlot = getLocalSelectedSlot('reservations.selected_slot');

        const currentTime = dayjs().unix();
        if (Evisit.c('Account.waiting_room.visits.appointment_v2.enable', false)) {
            data = _.filter(data, function (reservation) {
                return reservation.clinic_code === Evisit.c('Account.code') && reservation.id === localSelectedSlot?.id;
            });
        }

        data.forEach((reservation) => {
            const diffInSecond = reservation.expiry - currentTime > 0 ? reservation.expiry - currentTime : 0;
            if (diffInSecond > 0) {
                messageApi.open({
                    key: key,
                    type: 'info',
                    content: <MessageReservation
                        reservation={reservation}
                        callbackReserveSlot={(e) => {
                            handleReserveSlotExpired(e);
                        }}
                    />,
                    duration: diffInSecond,
                }).then((e) => {
                    if (reservation.expiry - dayjs().unix() <= 0) {
                        handleReserveSlotExpired(reservation, true);
                    }
                })
            }
        });
    }

    useEffect(() => {
        if (get(reservations, 'data.length')) {
            displayReservation()
        } else {
            messageApi.destroy(key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservations]);

    useEffect(() => {error && displayError(error)}, [error]);

    useEffect(() => {
        if (get(postResult, 'data.id')) {
            localStorage.setItem('reservations.selected_slot', JSON.stringify(postResult.data));

            refetchData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postResult]);

    useImperativeHandle(ref, () => ({
        /**
         * @param {
         *     slot_id: string,
         *     start: timestamp,
         *     end: timestamp
         * } slot - The slot object
         * @param {code: string} room - The room object
         * @param {string} provider_id - The provider id
         */
        reserve : async (slot, room, provider_id) => {
            const currentReservation = _.find(reservations?.data, {slot_id: slot.slot_id});
            const localSelectedSlot = getLocalSelectedSlot('reservations.selected_slot');

            if (currentReservation?.start !== slot.start) {
                await deleteData(`${window.base_url}/api/reservations/${localSelectedSlot?.id}`);
                await postCreateReservation({
                    clinic_code: Evisit.c('Account.code'),
                    room_code: room.code,
                    provider_id: provider_id,
                    slot_id: slot.slot_id,
                    slot_start: slot.start,
                    slot_end: slot.end,
                });
            }

        },
        removeReservation: async () => {
            const localSelectedSlot = getLocalSelectedSlot('reservations.selected_slot');
            await deleteData(`${window.base_url}/api/reservations/${localSelectedSlot?.id}`);
            localStorage.removeItem('reservations.selected_slot');
            refetchData()
        }
    }));

    return (
        <>
            <ReservationContext.Provider value={{
                refetchData
            }}>
                {contextHolder}
                <ConfirmReservation ref={confirmReservationRef}/>
            </ReservationContext.Provider>
        </>
    );
})

const MessageReservation = (props) => {
    const [waitingTime, setWaitingTime] = useState('');
    const {
        reservation,
        callbackReserveSlot
    } = props;

    useEffect(() => {
        if (reservation) {
            const expiryTime = reservation.expiry;
            setWaitingTime(getWaitingTime(expiryTime));

            const intervalId = setInterval(() => {
                setWaitingTime(getWaitingTime(expiryTime));
            }, 1000); // refresh every second

            // Clear interval on component unmount
            return () => clearInterval(intervalId);
        }
    }, [reservation]);


    return (
        <>
            {
                intl.get("reservation_message", {
                    waitingTime: waitingTime
                })
                    .d("This slot is reserved for {waitingTime}. Complete the steps to confirm scheduled appointment. You may also choose to release the slot and select a different date and time.")
            }
            <Space>
                <Button type="primary"
                    onClick={() => callbackReserveSlot(reservation)}
                    className={styles.btnReleaseSlot}
                >
                    {
                        intl.get("release_slot").d("Release Slot")
                    }
                </Button>
            </Space>
        </>
    )
}

const ConfirmReservation = forwardRef((props, ref) => {
    const [open, setOpen] = useState(false);
    const [expired, setExpired] = useState(false);
    const [reservation, setReservation] = useState(null);
    const { state : { isLoading, error }, deleteData, result } = useDeleteData(`${window.base_url}/api/reservations/${reservation?.id}`);
    const {refetchData} = React.useContext(ReservationContext);
    const handleOk = () => {
        deleteData()
    };
    const handleCancel = () => {
        setOpen(false);
    };

    useImperativeHandle(ref, () => ({
        showModal: (data, expired) => {
            setOpen(true);
            setReservation(data);
            setExpired(expired);
        }
    }));
    useEffect(() => {error && displayError(error)}, [error]);

    useEffect(() => {
        if (result) {
            message.success(intl.get("slot_released").d("You have released a slot. Please choose another date and time."));
            setOpen(false);
            refetchData();
        }
    }, [result]);

    return (
        <>
            <Modal
                open={open}
                title={intl.get("slot_reservation").d("Slot Reservation")}
                onOk={handleOk}
                onCancel={handleCancel}
                okText={intl.get("release_slot").d("Release Slot")}
                cancelText={intl.get("not_now").d("Not Now")}
                okButtonProps={{danger: true}}
                confirmLoading={isLoading}
                footer={(_, { OkBtn, CancelBtn }) => {
                    if (expired) {
                        return (
                            <>
                                <OkBtn/>
                                <RenewSlot
                                    reservation={reservation}
                                    successCallback={() => {
                                        setOpen(false);
                                    }}
                                />
                            </>
                        )
                    }
                    return (
                        <>
                            <CancelBtn/>
                            <OkBtn/>
                        </>
                    );
                }}
            >
                <p>
                    {
                        !expired
                            ? intl.get("release_slot_message")
                                .d("You are about to release this reserved slot. You may select another available date and time. Do you want to continue?")
                            : intl.get("slot_expired_message")
                                .d("Slot reservation time has expired. Do you wish to try to reserve the current date and time slot again?")
                    }
                </p>
            </Modal>
        </>
    );
})

const RenewSlot = (props) => {
    const {reservation, successCallback} = props;
    const {refetchData} = React.useContext(ReservationContext);
    const { state: {isLoading, error},
        putData,
        result
    } = useSubmitData(`${window.base_url}/api/reservations/${reservation?.id}/renew`);

    const canRenew = useMemo(() => {
        if (dayjs(reservation?.start).isBefore(
            dayjs().add(window.CONST.SLOT.TIME.APPOINTMENT.BLACKOUT, 'second'))
            && !Evisit.c('Visit.AllowPastAppointmentBooking', false)
        ) {
            return false;
        }
        return true;
    }, [reservation])

    const handleReserveSameSlot = () => {
        if (canRenew) {
            return putData();
        }

        message.error(intl.get("slot_not_available").d("Slot is not available for reservation. Please choose another date and time."));
        refetchData();
    }

    useEffect(() => {error && displayError(error)}, [error]);

    useEffect(() => {
        if (result) {
            message.success(intl.get("slot_reserved").d("Slot reservation has been renewed successfully."));
            successCallback();
            refetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    return (
        <Button type="primary" onClick={handleReserveSameSlot} loading={isLoading}>
            {intl.get("reserve_same_slot").d("Reserve Same Slot Again")}
        </Button>
    )
}

export default Reservation;